import { reviewMutations, reviewQueries } from './index';
import { useQueryData } from '../apollo/query';
import { useMutationData } from '../apollo/mutation';
import {
  ICreateReviewInput,
  IUpdateReviewInput,
  IDeleteReviewInput,
  IReview,
  IGetReviewInput,
  IReviewListInput,
  IQueryGetReviewArgs,
  IMutationCreateReviewArgs,
  IMutationUpdateReviewArgs,
  IMutationDeleteReviewArgs,
  IAverageReviewInput,
  IAverageReview,
  IQueryAverageReviewArgs,
  IQueryGetReviewListArgs,
} from 'types/types';

import { IError, ISuccess } from 'types/Models';
import { useTranslationText } from 'locale';

interface Callback {
  create: {
    loading: boolean;
    createReview: (input: ICreateReviewInput) => Promise<{ id?: string; error?: IError; success?: ISuccess }>;
  };
  update: {
    loading: boolean;
    updateReview: (input: IUpdateReviewInput) => Promise<{ error?: IError; success?: ISuccess }>;
  };
  delete: {
    loading: boolean;
    deleteReview: (input: IDeleteReviewInput) => Promise<{ error?: IError; success?: ISuccess }>;
  };
  get: {
    error?: IError;
    loading: boolean;
    Review?: IReview;
    getReview: (input: IGetReviewInput) => void;
  };
  list: {
    loading: boolean;
    reviews: IReview[];
    listReviews: (input: IReviewListInput) => void;
  };
  average: {
    loading: boolean;
    avg?: IAverageReview;
    averageReview: (input: IAverageReviewInput) => void;
  };
}

export const useReviewApi = (): Callback => {
  const { getMessage } = useTranslationText();

  //MARK: GET QURERY
  const [getReview, { data: dataGet, error: errorGet, loading: getLoading }] = useQueryData<
    { getReview: IReview },
    IQueryGetReviewArgs
  >({
    table: 'review',
    action: 'get',
    tData: 'getReview',
    fetchPolicy: 'cache-and-network',
    query: reviewQueries.GET,
  });

  //MARK: GET LIST
  const [listReviews, { data: dataList, loading: loadingList }] = useQueryData<
    { listReviews: IReview[] },
    IQueryGetReviewListArgs
  >({
    table: 'review',
    action: 'list',
    tData: 'listReviews',
    fetchPolicy: 'cache-and-network',
    query: reviewQueries.LIST,
  });

  const [averageReview, { data: averageReviewData, loading: averageReviewLoading }] = useQueryData<
    { averageReview: IAverageReview },
    IQueryAverageReviewArgs
  >({
    table: 'review',
    action: 'get',
    tData: 'averageReview',
    fetchPolicy: 'cache-and-network',
    query: reviewQueries.AVERAGE,
  });

  //MARK: CREATE MUTATION
  const [createReview, { loading: createLoading }] = useMutationData<
    { createReview: string },
    IMutationCreateReviewArgs
  >({
    mutation: reviewMutations.CREATE,
    table: 'review',
    action: 'add',
    tData: 'createReview',
  });

  //MARK: UPDATE MUTATION
  const [updateReview, { loading: updateLoading }] = useMutationData<
    { updateReview: boolean },
    IMutationUpdateReviewArgs
  >({
    mutation: reviewMutations.UPDATE,
    table: 'review',
    action: 'update',
  });

  //MARK: DELETE MUTATION
  const [deleteReview] = useMutationData<{ deleteReview: boolean }, IMutationDeleteReviewArgs>({
    mutation: reviewMutations.DELETE,
    table: 'review',
    action: 'delete',
  });

  /********************** CUSTOM FUNCTION ************************* */

  //MARK:CREATE FUNCTION
  async function mutationcreateReview(
    news: ICreateReviewInput,
  ): Promise<{ id?: string; error?: IError; success?: ISuccess }> {
    try {
      const create = await createReview({ variables: { input: news } });
      return { id: create.data?.createReview, success: getMessage('0', 'success') };
    } catch (error) {
      return { error: getMessage(error.message, 'error') };
    }
  }

  //MARK:UPDATE FUNCTION
  async function mutationupdateReview(news: IUpdateReviewInput): Promise<{ error?: IError; success?: ISuccess }> {
    try {
      await updateReview({ variables: { input: news } });
      return { success: getMessage('0', 'success') };
    } catch (error) {
      return { error: getMessage(error.message, 'error') };
    }
  }

  //MARK:CREATE FUNCTION
  async function mutationdeleteReview(
    deleteInput: IDeleteReviewInput,
  ): Promise<{ error?: IError; success?: ISuccess }> {
    try {
      await deleteReview({ variables: { input: deleteInput } });
      return { success: getMessage('0', 'success') };
    } catch (error) {
      return { error: getMessage(error.message, 'error') };
    }
  }

  async function querygetReview(getInput: IGetReviewInput) {
    getReview({ variables: { input: getInput } });
  }

  //MARK:GET FUNCTION
  function querylistReviews(listIput: IReviewListInput) {
    listReviews({ variables: { input: listIput } });
  }

  function avgReviews(listIput: IAverageReviewInput) {
    averageReview({ variables: { input: listIput } });
  }

  return {
    create: {
      loading: createLoading,
      createReview: mutationcreateReview,
    },
    update: {
      loading: updateLoading,
      updateReview: mutationupdateReview,
    },

    delete: {
      loading: createLoading,
      deleteReview: mutationdeleteReview,
    },
    get: {
      error: errorGet ? getMessage(errorGet.message, 'error') : undefined,
      Review: dataGet?.getReview,
      loading: dataGet?.getReview ? false : getLoading,
      getReview: querygetReview,
    },
    list: {
      loading: (dataList?.listReviews || []).length > 0 ? false : loadingList,
      reviews: dataList?.listReviews || [],
      listReviews: querylistReviews,
    },
    average: {
      loading: averageReviewData?.averageReview ? false : averageReviewLoading,
      avg: averageReviewData?.averageReview,
      averageReview: avgReviews,
    },
  };
};
